// ─────────────────────────────────────────────
// Oike · Mi marca › «Cuaderno de trabajo» (PDF A4) — pure: the template, the validation of its content and the
// layout. The layout is the single source of geometry: the preview (SVG) and the PDF draw exactly these items, with
// the same font files measured the same way, so line breaks and positions match. Tested by scripts/test-notebook.mjs.
//
// Rules agreed with Ale (04-11-2026):
// - Identity A «Pizarra» (src/brandIdentity.js); earlier pieces keep their own identity.
// - Cover with an image: the image has its own zone (below the title, on the right) and the text its own; they never
//   overlap, so no face has text or strokes on it. Without an image the cover recomposes (no empty hole).
// - Fixed bottom margin: nothing goes below BOTTOM. Text never shrinks to fit: what does not fit goes to the next page;
//   a paragraph longer than the space is split between lines (at least two lines on each side), never cut or lost.
// - A subtitle never ends a page: it goes with the first lines of what follows. Margin notes stay next to their text.
// ─────────────────────────────────────────────
import { IDENTITY_A } from './brandIdentity.js';
import * as S from './brandStrokes.js';

export const A4 = Object.freeze({ w: 595.28, h: 841.89 });
const W = A4.w, H = A4.h;
export const NB = Object.freeze({
  margin: 48, top: 54, bottom: H - 100, footerBaseline: H - 32,
  mainWidth: 352, noteX: 420, noteWidth: 127,
});
const C = IDENTITY_A.colors;
export const NOTEBOOK_TEMPLATE = 'cuaderno';
export const NOTEBOOK_FORMAT = 'pdf_a4';
export const NOTEBOOK_MAX_PAGES = 24;
export const SECTION_TYPES = Object.freeze({
  cover: { label: 'Portada' },
  reading: { label: 'Lectura' },
  scheme: { label: 'Esquema' },
  exercise: { label: 'Ejercicio' },
});
export const BLOCK_KINDS = Object.freeze({
  p: { label: 'Párrafo' }, h: { label: 'Subtítulo' }, list: { label: 'Lista' }, quote: { label: 'Frase destacada' }, box: { label: 'Recuadro' },
});
export const NB_LIMITS = Object.freeze({
  sections: 20, blocks: 60, listItems: 8, steps: 3, lines: { min: 2, max: 10 },
  kicker: 60, title: 90, highlight: 30, subtitle: 220, coverNote: 60, author: 60, footer: 80,
  label: 60, sectionTitle: 90, underline: 40, p: 4000, h: 120, item: 300, quote: 300, box: 700, boxTitle: 40, note: 70,
  intro: 600, exIntro: 400, stepTitle: 30, stepText: 120, exStepTitle: 80, hint: 140, keyText: 220, keyTitle: 40, json: 90_000,
});
const ID_RE = /^[a-z0-9]{4,16}$/;
const REC_RE = /^rec[A-Za-z0-9]{14}$/;

// ── Sample texts: a new notebook starts with them so the structure is visible. While one is still there, the
// notebook cannot be approved (never published with sample text). ──
export const NB_SAMPLE = Object.freeze({
  kicker: 'Cuaderno de trabajo · 01',
  title: 'Tu próximo paso profesional',
  highlight: 'paso',
  subtitle: 'Una guía breve para ordenar lo que querés cambiar y elegir una acción posible.',
  coverNote: '15 minutos · 3 pasos',
  readingLabel: 'Capítulo 1 · Lectura',
  readingTitle: 'Leer un cambio sin perderte',
  readingUnderline: 'sin perderte',
  lead: 'Un cambio importante rara vez llega ordenado. Esta lectura propone tres movimientos sencillos para mirarlo con más calma. (Texto de ejemplo: reemplazalo.)',
  h1: '1. Nombrar lo que cambió',
  p1: 'Lo primero es ponerle palabras a lo que efectivamente cambió: un puesto, un país, un equipo, una rutina. Escribirlo en una frase corta ayuda a separar el hecho de las interpretaciones. (Texto de ejemplo: reemplazalo.)',
  note1: 'un hecho, no una etiqueta',
  quote: 'Lo importante no grita. Por eso hay que reservarle un lugar.',
  boxTitle: 'Para llevarte',
  box: 'Nombrá lo que cambió y elegí una prueba pequeña para esta semana. (Texto de ejemplo: reemplazalo.)',
  schemeLabel: 'Capítulo 2 · La idea',
  schemeTitle: 'Del cambio a la acción',
  schemeUnderline: 'a la acción',
  schemeIntro: 'Ordenar el cambio en tres preguntas lo vuelve más manejable: dónde estás, qué querés y qué podés probar ahora. (Texto de ejemplo: reemplazalo.)',
  schemeNote: 'no hace falta tener todo claro para empezar',
  steps: [['Dónde estoy', 'Lo que hacés hoy y lo que te cuesta.'], ['Qué quiero', 'Un cambio concreto, con tus palabras.'], ['Qué puedo probar', 'Una acción pequeña para esta semana.']],
  keyTitle: 'Idea clave',
  keyText: 'Un paso pequeño y concreto vale más que un plan perfecto que no empieza.',
  exLabel: 'Ejercicio · 15 minutos',
  exTitle: 'Tres pasos para elegir',
  exUnderline: 'elegir',
  exIntro: 'Respondé con frases cortas. No hay respuestas correctas. (Texto de ejemplo: reemplazalo.)',
  exSteps: [['¿Dónde estás hoy?', 'Tu trabajo, lo que hacés bien y lo que te gustaría dejar.', 4], ['¿Qué querés cambiar?', 'Una sola cosa, lo más concreta posible.', 4], ['Elegí una acción para esta semana', 'Algo que puedas hacer en menos de una hora.', 3]],
  exNote: '¡esta es la importante!',
});
const SAMPLE_VALUES = new Set(Object.values(NB_SAMPLE).flatMap(v => (Array.isArray(v) ? v.flat() : [v])).filter(v => typeof v === 'string' && v.length > 6));

let idSeq = 0;
export const newId = () => `${Date.now().toString(36).slice(-6)}${(idSeq++).toString(36)}${Math.random().toString(36).slice(2, 6)}`.slice(0, 16).padEnd(4, '0');

export function newSection(type) {
  const s = NB_SAMPLE;
  if (type === 'cover') return { id: newId(), type, kicker: s.kicker, title: s.title, highlight: s.highlight, subtitle: s.subtitle, note: s.coverNote, author: '', image: null };
  if (type === 'reading') return { id: newId(), type, label: s.readingLabel, title: s.readingTitle, underline: s.readingUnderline, blocks: [
    { id: newId(), kind: 'p', lead: true, text: s.lead },
    { id: newId(), kind: 'h', text: s.h1 },
    { id: newId(), kind: 'p', text: s.p1, note: s.note1 },
    { id: newId(), kind: 'quote', text: s.quote },
    { id: newId(), kind: 'box', title: s.boxTitle, text: s.box },
  ] };
  if (type === 'scheme') return { id: newId(), type, label: s.schemeLabel, title: s.schemeTitle, underline: s.schemeUnderline, intro: s.schemeIntro, note: s.schemeNote, steps: s.steps.map(([title, text]) => ({ title, text })), keyTitle: s.keyTitle, keyText: s.keyText };
  if (type === 'exercise') return { id: newId(), type, label: s.exLabel, title: s.exTitle, underline: s.exUnderline, intro: s.exIntro, steps: s.exSteps.map(([title, hint, lines]) => ({ title, hint, lines })), note: s.exNote, noteStep: 2 };
  throw new Error('unknown section');
}
export function newNotebook() {
  return { identity: { key: IDENTITY_A.key, version: IDENTITY_A.version }, footer: '', sections: ['cover', 'reading', 'scheme', 'exercise'].map(newSection) };
}
export const newBlock = (kind) => (kind === 'list' ? { id: newId(), kind, items: [''] } : kind === 'box' ? { id: newId(), kind, title: '', text: '' } : { id: newId(), kind, text: '' });

// ── Validation (shared by the screen and the server). fail(message) must throw. ──
export function notebookInput(v, fail = (m) => { throw new Error(m); }) {
  const c = v && typeof v === 'object' ? v : {};
  const str = (x, max, what) => {
    if (x === undefined || x === null) return '';
    if (typeof x !== 'string') fail(`invalid ${what}`);
    const t = x.replace(/\r\n?/g, '\n').replace(/[ \t]+\n/g, '\n').trim();
    if (t.length > max) fail(`too long: ${what}`);
    return t;
  };
  const num = (x, lo, hi, d) => (Number.isFinite(Number(x)) ? Math.min(hi, Math.max(lo, Number(x))) : d);
  const id = (x) => (typeof x === 'string' && ID_RE.test(x) ? x : newId());
  const L = NB_LIMITS;
  if (!c.identity || c.identity.key !== IDENTITY_A.key) fail('invalid identity');
  const sections = Array.isArray(c.sections) ? c.sections : [];
  if (!sections.length) fail('missing sections');
  if (sections.length > L.sections) fail('too many sections');
  const seen = new Set();
  const out = sections.map((s, i) => {
    const type = s?.type;
    if (!SECTION_TYPES[type]) fail('invalid section');
    if ((type === 'cover') !== (i === 0)) fail('cover first');
    let sid = id(s.id); while (seen.has(sid)) sid = newId(); seen.add(sid);
    if (type === 'cover') {
      const img = s.image && REC_RE.test(String(s.image.photoId || '')) ? { photoId: s.image.photoId, x: num(s.image.x, -1, 1, 0), y: num(s.image.y, -1, 1, 0), zoom: num(s.image.zoom, 0.6, 1.6, 1) } : null;
      return { id: sid, type, kicker: str(s.kicker, L.kicker, 'kicker'), title: str(s.title, L.title, 'title'), highlight: str(s.highlight, L.highlight, 'highlight'), subtitle: str(s.subtitle, L.subtitle, 'subtitle'), note: str(s.note, L.coverNote, 'note'), author: str(s.author, L.author, 'author'), image: img };
    }
    const head = { id: sid, type, label: str(s.label, L.label, 'label'), title: str(s.title, L.sectionTitle, 'title'), underline: str(s.underline, L.underline, 'underline') };
    if (type === 'reading') {
      const blocks = Array.isArray(s.blocks) ? s.blocks : [];
      if (blocks.length > L.blocks) fail('too many blocks');
      return { ...head, blocks: blocks.map(b => {
        const kind = BLOCK_KINDS[b?.kind] ? b.kind : fail('invalid block');
        const base = { id: id(b.id), kind, note: str(b.note, L.note, 'note') };
        if (kind === 'list') { const items = (Array.isArray(b.items) ? b.items : []).slice(0, L.listItems + 1); if (items.length > L.listItems) fail('too many items'); return { ...base, items: items.map(x => str(x, L.item, 'item')) }; }
        if (kind === 'box') return { ...base, title: str(b.title, L.boxTitle, 'boxTitle'), text: str(b.text, L.box, 'box') };
        if (kind === 'p') return { ...base, lead: b.lead === true, text: str(b.text, L.p, 'paragraph') };
        return { ...base, text: str(b.text, kind === 'h' ? L.h : L.quote, kind) };
      }) };
    }
    if (type === 'scheme') {
      const steps = Array.isArray(s.steps) ? s.steps : [];
      if (steps.length !== 3) fail('scheme needs three steps');
      return { ...head, intro: str(s.intro, L.intro, 'intro'), note: str(s.note, L.note, 'note'), steps: steps.map(x => ({ title: str(x?.title, L.stepTitle, 'stepTitle'), text: str(x?.text, L.stepText, 'stepText') })), keyTitle: str(s.keyTitle, L.keyTitle, 'keyTitle'), keyText: str(s.keyText, L.keyText, 'keyText') };
    }
    const steps = Array.isArray(s.steps) ? s.steps : [];
    if (!steps.length || steps.length > L.steps) fail('exercise needs one to three steps');
    return { ...head, intro: str(s.intro, L.exIntro, 'intro'), note: str(s.note, L.note, 'note'), noteStep: Math.round(num(s.noteStep, 0, steps.length - 1, steps.length - 1)), steps: steps.map(x => ({ title: str(x?.title, L.exStepTitle, 'stepTitle'), hint: str(x?.hint, L.hint, 'hint'), lines: Math.round(num(x?.lines, L.lines.min, L.lines.max, 4)) })) };
  });
  const content = { identity: { key: IDENTITY_A.key, version: IDENTITY_A.version }, footer: str(c.footer, L.footer, 'footer'), sections: out };
  if (JSON.stringify(content).length > L.json) fail('too long: notebook');
  return content;
}

/** The photos a notebook uses (to check their authorization before approving or exporting). */
export const notebookPhotoIds = (content) => (content?.sections || []).map(s => s.image?.photoId).filter(Boolean);

/**
 * Notices that never block approval (Ale decides, agreed on 04-11-2026): sample texts still there, an empty reading
 * part, no cover title, only a cover. The PDF can be generated with any of them.
 */
export function notebookWarnings(content) {
  const issues = [];
  const cover = content?.sections?.[0];
  if (!cover || cover.type !== 'cover') issues.push('missing:cover');
  else if (!cover.title) issues.push('missing:title');
  if ((content?.sections || []).length < 2) issues.push('missing:pages');
  const texts = [];
  for (const s of content?.sections || []) {
    texts.push(s.kicker, s.title, s.subtitle, s.note, s.label, s.intro, s.keyText, s.keyTitle);
    for (const b of s.blocks || []) texts.push(b.text, b.title, b.note, ...(b.items || []));
    for (const st of s.steps || []) texts.push(st.title, st.text, st.hint);
    if (s.type === 'reading' && !(s.blocks || []).some(b => (b.text || (b.items || []).join('')).trim())) issues.push(`empty:${s.id}`);
  }
  if (texts.some(t => t && (SAMPLE_VALUES.has(t) || /\(Texto de ejemplo: reemplazalo\.\)/.test(t)))) issues.push('sample_text');
  return [...new Set(issues)];
}
/** Layout problems that keep the PDF from being generated correctly (text that would overlap or leave its page). */
export const NB_BLOCKING = Object.freeze(['cover_text_long', 'block_too_tall', 'scheme_too_long', 'step_title_long', 'step_too_tall', 'image_loading']);

// ── Layout ──
// measure(text, fontKey, size) → width in points (the same font files as the PDF, without kerning, as pdf-lib draws).
// images: { [photoId]: { w, h } } natural size of each photo (when known).

/** Lines of a text in a width: words kept whole (a word longer than the line is split by letters). */
export function wrap(text, font, size, width, measure) {
  const out = [];
  for (const para of String(text || '').split('\n')) {
    const words = para.split(/\s+/).filter(Boolean);
    if (!words.length) { out.push(''); continue; }
    let line = '';
    for (const w0 of words) {
      let w = w0;
      while (measure(w, font, size) > width) { // a very long word: split by letters
        let k = w.length - 1; while (k > 1 && measure(w.slice(0, k), font, size) > width) k--;
        if (line) { out.push(line); line = ''; }
        out.push(w.slice(0, k)); w = w.slice(k);
      }
      const tryLine = line ? `${line} ${w}` : w;
      if (measure(tryLine, font, size) <= width) line = tryLine; else { out.push(line); line = w; }
    }
    if (line) out.push(line);
  }
  return out;
}
const norm = (t) => String(t || '').toLowerCase();
/** Where a phrase sits in some laid-out lines: [{ line, x, w }] (one per line it covers), or []. */
function findPhrase(lines, phrase, font, size, measure) {
  const p = norm(phrase).trim();
  if (!p) return [];
  for (let i = 0; i < lines.length; i++) {
    const at = norm(lines[i].text).indexOf(p);
    if (at >= 0) return [{ line: lines[i], x: lines[i].x + measure(lines[i].text.slice(0, at), font, size), w: measure(lines[i].text.slice(at, at + p.length), font, size) }];
  }
  return [];
}
const baseline = (top, lh, size) => top + (lh - size) / 2 + size * 0.8;

export function layoutNotebook(content, { measure, images = {}, author = 'Alejandra Cadario', footerTitle = '' } = {}) {
  const pages = []; const issues = [];
  const sections = content?.sections || [];
  const footerText = `${author}${(content.footer || footerTitle || sections[0]?.title) ? ` · ${content.footer || footerTitle || sections[0].title}` : ''}`;
  const M = NB.margin;
  let pageNo = 0;
  const newPage = (section, kind, dark = false) => {
    pageNo += 1;
    const page = { n: pageNo, section: section.id, type: section.type, kind, dark, items: [], notes: [] };
    page.items.push({ t: 'rect', x: 0, y: 0, w: W, h: H, fill: dark ? C.carbon : C.papel });
    if (dark) page.items.push({ t: 'texture', x: 0, y: 0, w: W, h: H });
    pages.push(page);
    return page;
  };
  const text = (page, x, y, str, font, size, color) => { if (str) page.items.push({ t: 'text', x, y, text: str, font, size, color }); };
  const strokes = (page, list, color, width, extra = {}) => { for (const s of list) page.items.push({ t: 'path', d: s.d, stroke: color, width: s.pass ? width * 0.68 : width, opacity: s.pass ? 0.55 : 1, ...extra }); };
  const lines = (str, font, size, width, x, top, lh) => wrap(str, font, size, width, measure).map((t, i) => ({ text: t, x, top: top + i * lh, lh, size, font }));
  const drawLines = (page, ls, color) => { for (const l of ls) text(page, l.x, baseline(l.top, l.lh, l.size), l.text, l.font, l.size, color); };
  const footer = (page) => {
    text(page, M, NB.footerBaseline, footerText, 'bodyBold', 7.6, C.gris);
    const n = String(page.n); text(page, W - M - measure(n, 'bodyBold', 7.6), NB.footerBaseline, n, 'bodyBold', 7.6, C.gris);
  };
  const label = (page, str, y, color) => text(page, M, y, str.toUpperCase(), 'bodyHeavy', 8.2, color);
  // A section title on a light page, with its underlined phrase; returns its bottom
  const sectionHead = (page, s, labelColor, top = NB.top) => {
    label(page, s.label || '', top + 6, labelColor);
    const tl = lines(s.title || '', 'title', 42, W - 2 * M, M, top + 14, 42);
    drawLines(page, tl, C.petroleo);
    const u = findPhrase(tl, s.underline, 'title', 42, measure)[0];
    if (u) strokes(page, S.underline(u.x - 2, u.line.top + 42, u.x + u.w + 4, u.line.top + 38, S.seedOf(`${s.id}u`), { wob: 2 }), C.turquesa, 4.6);
    if (tl.length > 2) issues.push({ section: s.id, code: 'title_lines', field: 'title' });
    return top + 14 + tl.length * 42;
  };

  for (const [si, s] of sections.entries()) {
    const seed = (k) => S.seedOf(`${s.id}:${k}`);
    if (s.type === 'cover') {
      const page = newPage(s, 'cover', true);
      const img = s.image?.photoId ? s.image : null;
      const dims = img ? images[img.photoId] : null;
      text(page, M, 66, (s.kicker || '').toUpperCase(), 'bodyHeavy', 9, C.turquesa);
      // Title: as large as it fits in three lines (a title, never a paragraph)
      const maxW = W - 2 * M;
      let size = img ? 76 : 100; const minSize = img ? 52 : 66;
      let tl = [];
      for (; size >= minSize; size -= 2) { tl = wrap(s.title || '', 'title', size, maxW, measure); if (tl.length <= 3) break; }
      size = Math.max(size, minSize);
      if (tl.length > 3) issues.push({ section: s.id, code: 'cover_title_long', field: 'title' });
      const lh = size * 0.94;
      const top = img ? 84 : 132;
      const tLines = tl.map((t, i) => ({ text: t, x: M, top: top + i * lh, lh, size, font: 'title' }));
      // The highlighted word: a turquoise marker block behind it (drawn first); with an image it reaches the edge
      const hl = findPhrase(tLines, s.highlight, 'title', size, measure)[0];
      if (hl) {
        const bx = hl.x - 10, by = hl.line.top + lh * 0.1, bh = lh * 0.86;
        const bw = img ? W - bx + 6 : hl.w + 22;
        page.items.push({ t: 'path', d: S.block(bx, by, bw, bh, seed('hl')), fill: C.turquesa, stroke: null, width: 0 });
        strokes(page, S.sparks(bx - 6, by + 10, seed('sp'), { r: size * 0.32 }), C.amarillo, 3.2);
      }
      drawLines(page, tLines, C.tiza);
      const titleBottom = top + tLines.length * lh;
      const last = tLines[tLines.length - 1];
      if (last) strokes(page, S.underline(M + 4, titleBottom + 8, M + Math.min(maxW, measure(last.text, 'title', size) + 24), titleBottom + 2, seed('ul')), C.coral, 6.4);
      let yText;
      if (img) {
        // Image zone (right, below the title) and text zone (left): they never overlap
        const zone = { x: 292, y: titleBottom + 28, w: W - 292, h: H - (titleBottom + 28) };
        page.imageZone = zone;
        if (dims?.w && dims?.h) {
          const scale = (zone.h * 0.96 * img.zoom) / dims.h;
          const w = dims.w * scale, h = dims.h * scale;
          const x = zone.x + (zone.w - w) / 2 + img.x * zone.w * 0.5;
          const y = zone.y + zone.h - h + img.y * zone.h * 0.5 + (img.y > 0 ? 0 : 0);
          page.items.push({ t: 'image', photoId: img.photoId, x, y, w, h, clip: zone });
        } else issues.push({ section: s.id, code: 'image_loading', field: 'image' });
        const sub = lines(s.subtitle || '', 'body', 13.4, zone.x - M - 26, M, zone.y + 4, 20.2);
        drawLines(page, sub, C.tizaSoft);
        yText = zone.y + 4 + sub.length * 20.2;
      } else {
        const sub = lines(s.subtitle || '', 'body', 15.4, 380, M, titleBottom + 44, 23);
        drawLines(page, sub, C.tizaSoft);
        yText = titleBottom + 44 + sub.length * 23;
        // Without an image: a short 1 → 2 → 3 scheme balances the page (no empty hole)
        const y = H - 132, xs = [W - M - 168, W - M - 100, W - M - 32];
        [C.turquesa, C.coral, C.amarillo].forEach((col, i) => strokes(page, S.loop(xs[i], y, 20, 18, seed(`c${i}`)), col, 3.2));
        for (let i = 0; i < 2; i++) strokes(page, S.arrow(xs[i] + 23, y, xs[i + 1] - 24, y, seed(`a${i}`), { bend: 3, head: 7 }), C.tiza, 2.2);
      }
      if (s.note) {
        // The note keeps to the text zone (wrapped in its width): never into the image zone
        const ny = yText + 34;
        const nw = (page.imageZone ? page.imageZone.x - 16 : W - M) - (M + 26);
        const nl = lines(s.note, 'note', 22, nw, M + 26, ny, 25);
        drawLines(page, nl, C.amarillo);
        strokes(page, S.arrow(M + 16, ny + 14, M + 8, yText + 4, seed('na'), { bend: 8, head: 8 }), C.amarillo, 2.4);
        yText = ny + nl.length * 25;
      }
      if (yText > H - 84) issues.push({ section: s.id, code: 'cover_text_long', field: s.note ? 'note' : 'subtitle' });
      text(page, M, H - 48, (s.author || author).toUpperCase(), 'bodyHeavy', 9.4, C.tiza);
      continue;
    }

    if (s.type === 'reading') {
      // Flow units: lines that can split (paragraphs, list items) and blocks that cannot (subtitles, quotes, boxes)
      const units = [];
      for (const b of s.blocks || []) {
        if (b.kind === 'p' && b.text) {
          const size = b.lead ? 13.2 : 11.4, lh = b.lead ? 21 : 18.8, font = 'body';
          units.push({ kind: 'lines', block: b, lines: wrap(b.text, font, size, NB.mainWidth, measure), size, lh, font, color: b.lead ? C.tinta : C.texto, gapAfter: 9, note: b.note });
        } else if (b.kind === 'h' && b.text) {
          const ls = wrap(b.text, 'title', 24, NB.mainWidth, measure);
          units.push({ kind: 'h', block: b, lines: ls, height: 14 + ls.length * 25 + 4, note: b.note });
        } else if (b.kind === 'list') {
          const items = (b.items || []).filter(x => x.trim());
          items.forEach((it, k) => units.push({ kind: 'lines', block: b, item: k, lines: wrap(it, 'body', 11.4, NB.mainWidth - 18, measure), size: 11.4, lh: 18.4, font: 'body', color: C.texto, indent: 18, bullet: true, gapAfter: k === items.length - 1 ? 10 : 4, note: k === 0 ? b.note : '' }));
        } else if (b.kind === 'quote' && b.text) {
          const ls = wrap(b.text, 'title', 26, NB.mainWidth, measure);
          units.push({ kind: 'quote', block: b, lines: ls, height: 18 + ls.length * 28 + 22, note: b.note });
        } else if (b.kind === 'box' && (b.text || b.title)) {
          const ls = wrap(b.text || '', 'body', 11.4, NB.mainWidth - 28, measure);
          units.push({ kind: 'box', block: b, lines: ls, height: 14 + 16 + (b.title ? 16 : 0) + ls.length * 18.4 + 14 + 14, note: b.note });
        }
      }
      let page = newPage(s, 'reading');
      let y = sectionHead(page, s, C.turquesaTexto) + 20;
      const startTop = y;
      const next = () => { footer(page); placeNotes(page); page = newPage(s, 'reading-cont'); y = NB.top; };
      const fresh = () => y <= (page.kind === 'reading' ? startTop : NB.top) + 0.5;
      for (let ui = 0; ui < units.length; ui++) {
        const u = units[ui];
        const room = () => NB.bottom - y;
        if (u.kind === 'h') {
          // A subtitle travels with the first two lines (or the whole) of what follows
          const n = units[ui + 1];
          const nextNeed = !n ? 0 : n.kind === 'lines' ? Math.min(2, n.lines.length) * n.lh : n.height;
          const before = fresh() ? 0 : 14;
          if (before + u.height - 14 + nextNeed > room() && !fresh()) next();
          const top = y + (fresh() ? 0 : 14);
          const ls = u.lines.map((t, i) => ({ text: t, x: NB.margin, top: top + i * 25, lh: 25, size: 24, font: 'title' }));
          drawLines(page, ls, C.petroleo);
          if (u.note) page.notes.push({ y: top, text: u.note, anchorRight: NB.margin + Math.max(...ls.map(l => measure(l.text, 'title', 24))) });
          y = top + u.lines.length * 25 + 4;
          continue;
        }
        if (u.kind === 'quote' || u.kind === 'box') {
          if (u.height > room() && !fresh()) next();
          if (u.kind === 'quote') {
            const top = y + 18;
            const ls = u.lines.map((t, i) => ({ text: t, x: NB.margin + 2, top: top + i * 28, lh: 28, size: 26, font: 'title' }));
            strokes(page, S.sparks(NB.margin - 4, top + 6, S.seedOf(`${u.block.id}s`), { r: 16 }), C.amarillo, 2.6);
            drawLines(page, ls, C.petroleo);
            const lw = measure(ls[ls.length - 1].text, 'title', 26);
            strokes(page, S.underline(NB.margin + 4, top + ls.length * 28 + 4, NB.margin + Math.min(NB.mainWidth, lw + 10), top + ls.length * 28, S.seedOf(`${u.block.id}u`)), C.coral, 4.2);
            if (u.note) page.notes.push({ y: top, text: u.note, anchorRight: NB.margin + lw });
          } else {
            const top = y + 14, h = u.height - 14 - 14;
            page.items.push({ t: 'rect', x: NB.margin, y: top, w: NB.mainWidth, h, fill: C.panel });
            let ty = top + 16;
            if (u.block.title) { text(page, NB.margin + 14, ty + 6, u.block.title.toUpperCase(), 'bodyHeavy', 7.8, C.coralTexto); ty += 16; }
            drawLines(page, u.lines.map((t, i) => ({ text: t, x: NB.margin + 14, top: ty + i * 18.4, lh: 18.4, size: 11.4, font: 'body' })), C.texto);
            if (u.note) page.notes.push({ y: top, text: u.note, anchorRight: NB.margin + NB.mainWidth });
          }
          if (u.height > NB.bottom - NB.top) issues.push({ section: s.id, code: 'block_too_tall', field: `block:${u.block.id}` });
          y += u.height;
          continue;
        }
        // Lines: split between lines when they do not fit (at least two lines on each side; never shrunk or lost)
        let rest = u.lines; let first = true;
        while (rest.length) {
          const fit = Math.floor((room() + 0.01) / u.lh);
          let take;
          if (fit >= rest.length) take = rest.length;
          else if (fit < 2 || (rest.length <= 3 && !fresh())) { if (!fresh()) { next(); continue; } take = Math.max(1, fit); }
          else take = rest.length - fit < 2 ? rest.length - 2 : fit;
          if (take < 1) { next(); continue; }
          const part = rest.slice(0, take);
          const x = NB.margin + (u.indent || 0);
          const ls = part.map((t, i) => ({ text: t, x, top: y + i * u.lh, lh: u.lh, size: u.size, font: u.font }));
          if (u.bullet && first) page.items.push({ t: 'path', d: S.dash(NB.margin + 1, baseline(y, u.lh, u.size) - u.size * 0.32), stroke: C.turquesa, width: 2.4 });
          drawLines(page, ls, u.color);
          if (first && u.note) page.notes.push({ y, text: u.note, anchorRight: NB.margin + NB.mainWidth });
          y += part.length * u.lh;
          rest = rest.slice(take); first = false;
          if (rest.length) next();
        }
        y += u.gapAfter;
      }
      footer(page); placeNotes(page);
      continue;
    }

    if (s.type === 'scheme') {
      const page = newPage(s, 'scheme');
      let y = sectionHead(page, s, C.turquesaTexto) + 22;
      const intro = lines(s.intro || '', 'body', 11.6, 320, M, y, 19);
      drawLines(page, intro, C.texto);
      let noteBottom = y;
      if (s.note) {
        const nl = lines(s.note, 'note', 19, 135, 412, y + 2, 19);
        drawLines(page, nl, C.coralTexto);
        noteBottom = y + 2 + nl.length * 19;
      }
      const cy = Math.max(y + intro.length * 19, noteBottom) + 76;
      const xs = [M + 76, W / 2, W - M - 76];
      const cols = [C.turquesa, C.coral, C.amarillo];
      s.steps.forEach((st, i) => {
        strokes(page, S.loop(xs[i], cy, i === 2 ? 76 : 72, i === 2 ? 56 : 52, seed(`l${i}`)), cols[i], i === 2 ? 4.4 : 3.4);
        const tl = wrap(st.title || '', 'title', 23, 118, measure);
        if (tl.length > 3) issues.push({ section: s.id, code: 'step_title_long', field: `step:${i}` }); // said, never cut
        tl.forEach((t, k) => { const w = measure(t, 'title', 23); text(page, xs[i] - w / 2, baseline(cy - (tl.length * 23) / 2 + k * 23, 23, 23), t, 'title', 23, C.petroleo); });
        const sl = wrap(st.text || '', 'body', 10, 140, measure);
        sl.forEach((t, k) => { const w = measure(t, 'body', 10); text(page, xs[i] - w / 2, baseline(cy + 70 + k * 15, 15, 10), t, 'body', 10, C.texto); });
      });
      for (let i = 0; i < 2; i++) strokes(page, S.arrow(xs[i] + 76, cy - 6, xs[i + 1] - 78, cy - 6, seed(`a${i}`), { bend: 6, head: 9 }), C.petroleo, 2.6);
      if (s.note) strokes(page, S.arrow(470, noteBottom + 6, xs[2] + 8, cy - 62, seed('na'), { bend: 10, head: 9 }), C.coral, 2.3);
      let by = cy + 70 + 3 * 15 + 30;
      if (s.keyText) {
        const kl = wrap(s.keyText, 'body', 12.2, W - 2 * M - 40, measure);
        const h = 16 + (s.keyTitle ? 16 : 0) + kl.length * 19 + 16;
        page.items.push({ t: 'rect', x: M, y: by, w: W - 2 * M, h, fill: C.panel });
        let ty = by + 16;
        if (s.keyTitle) { text(page, M + 20, ty + 6, s.keyTitle.toUpperCase(), 'bodyHeavy', 7.8, C.coralTexto); strokes(page, S.underline(M + 20, ty + 10, M + 20 + measure(s.keyTitle.toUpperCase(), 'bodyHeavy', 7.8) + 10, ty + 9, seed('kt'), { passes: 1 }), C.coral, 1.8); ty += 16; }
        drawLines(page, kl.map((t, k) => ({ text: t, x: M + 20, top: ty + k * 19, lh: 19, size: 12.2, font: 'body' })), C.texto);
        by += h;
      }
      if (by > NB.bottom) issues.push({ section: s.id, code: 'scheme_too_long', field: 'intro' });
      footer(page);
      continue;
    }

    if (s.type === 'exercise') {
      let page = newPage(s, 'exercise');
      let y = sectionHead(page, s, C.coralTexto) + 18;
      const intro = lines(s.intro || '', 'body', 11.6, 420, M, y, 19);
      drawLines(page, intro, C.texto);
      y += intro.length * 19 + 30;
      const cols = [[C.turquesa, C.turquesaTexto], [C.coral, C.coralTexto], [C.amarillo, C.amarilloTexto]];
      s.steps.forEach((st, i) => {
        const hasNote = s.note && s.noteStep === i;
        const tx = M + 62, tw = W - M - tx - (hasNote ? NB.noteWidth + 14 : 0);
        const tl = wrap(st.title || '', 'bodyHeavy', 14, tw, measure);
        const hl = st.hint ? wrap(st.hint, 'body', 10, W - M - tx, measure) : [];
        const need = tl.length * 19 + hl.length * 14 + 10 + st.lines * 25.5 + 26;
        if (y + need > NB.bottom) {
          footer(page);
          page = newPage(s, 'exercise-cont');
          label(page, `${s.label || ''}${s.label ? ' · ' : ''}continúa`, NB.top + 6, C.coralTexto);
          y = NB.top + 30;
          if (y + need > NB.bottom) issues.push({ section: s.id, code: 'step_too_tall', field: `lines:${i}` });
        }
        const num = String(i + 1).padStart(2, '0');
        const nw = measure(num, 'title', 22);
        text(page, M + 22 - nw / 2, baseline(y - 4, 26, 22), num, 'title', 22, cols[i % 3][1]);
        strokes(page, S.loop(M + 22, y + 9, 22, 19, seed(`n${i}`)), cols[i % 3][0], 2.6);
        drawLines(page, tl.map((t, k) => ({ text: t, x: tx, top: y + k * 19, lh: 19, size: 14, font: 'bodyHeavy' })), C.tinta);
        if (hasNote) {
          const nl = wrap(s.note, 'note', 19, NB.noteWidth, measure);
          const nx = W - M - NB.noteWidth, ny = y - 18;
          drawLines(page, nl.map((t, k) => ({ text: t, x: nx, top: ny + k * 19, lh: 19, size: 19, font: 'note' })), C.coralTexto);
          const tEnd = tx + Math.max(...tl.map(t => measure(t, 'bodyHeavy', 14)));
          strokes(page, S.arrow(nx - 4, ny + 12, Math.min(nx - 18, tEnd + 8), y + 9, seed('na'), { bend: -8, head: 8 }), C.coral, 2.2);
        }
        let ly = y + tl.length * 19;
        if (hl.length) { drawLines(page, hl.map((t, k) => ({ text: t, x: tx, top: ly + k * 14, lh: 14, size: 10, font: 'body' })), C.gris); ly += hl.length * 14; }
        ly += 10;
        for (let k = 1; k <= st.lines; k++) page.items.push({ t: 'rect', x: tx, y: ly + k * 25.5, w: W - M - tx, h: 0.9, fill: C.linea });
        y = ly + st.lines * 25.5 + 26 + 10;
      });
      footer(page);
      continue;
    }
  }
  if (pages.length > NOTEBOOK_MAX_PAGES) issues.push({ section: null, code: 'too_many_pages' });
  return { pages, issues, size: { w: W, h: H } };

  // Margin notes next to their text: in order, never overlapping, never below the bottom margin
  function placeNotes(page) {
    let prev = -Infinity;
    const colors = [C.coralTexto, C.turquesaTexto];
    const strokesOf = [C.coral, C.turquesa];
    page.notes.sort((a, b) => a.y - b.y).forEach((n, i) => {
      const nl = wrap(n.text, 'note', 17, NB.noteWidth, measure);
      const h = nl.length * 17;
      let top = Math.max(n.y, prev + 8);
      if (top + h > NB.bottom) top = Math.max(prev + 8, NB.bottom - h);
      nl.forEach((t, k) => page.items.push({ t: 'text', x: NB.noteX, y: baseline(top + k * 17, 17, 17), text: t, font: 'note', size: 17, color: colors[i % 2] }));
      for (const s of S.arrow(NB.noteX - 4, top + 8, Math.min(NB.noteX - 16, n.anchorRight + 10), Math.max(n.y + 8, top + 18), S.seedOf(`${page.n}n${i}`), { bend: -7, head: 7 })) page.items.push({ t: 'path', d: s.d, stroke: strokesOf[i % 2], width: 2, opacity: 1 });
      prev = top + h;
    });
  }
}

/** Plain text of each page, in drawing order (to compare the preview with the PDF and to check nothing is lost). */
export const pageTexts = (layout) => layout.pages.map(p => p.items.filter(i => i.t === 'text').map(i => i.text));
